import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Phone, MapPin, Building2, FileText, Receipt, Calendar, CheckCircle, Clock, AlertCircle, Plus } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import { Invoice } from "../types";

interface Contact {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: {
    formatted?: string;
  };
  isClient?: boolean;
  isSupplier?: boolean;
}

interface Bill {
  _id: string;
  billNumber: string;
  date?: Date | string;
  dueDate?: Date | string;
  status: string;
  total: number;
  amountDue?: number;
  amountPaid?: number;
  currencyCode?: string;
  reference?: string;
}

interface ClientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: string;
  clientName: string;
  isSupplier?: boolean;
}

export const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  isSupplier = false
}) => {
  const { t } = useTranslation();
  const [contact, setContact] = useState<Contact | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchContact = useCallback(async () => {
    setLoadingContact(true);
    try {
      const token = localStorage.getItem('token');
      
      // First try to find contact by ID if clientId is provided
      if (clientId) {
        const response = await fetch(`${apiUrl}/api/contacts/${clientId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setContact(data.data);
            setLoadingContact(false);
            return;
          }
        }
      }
      
      // If not found by ID, try searching by name
      if (clientName) {
        const searchResponse = await fetch(`${apiUrl}/api/contacts?search=${encodeURIComponent(clientName)}&limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          if (searchData.success && searchData.data && searchData.data.length > 0) {
            // Find exact match by name
            const exactMatch = searchData.data.find((c: Contact) => 
              c.name.toLowerCase() === clientName.toLowerCase()
            );
            if (exactMatch) {
              setContact(exactMatch);
              setLoadingContact(false);
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch contact:', error);
    } finally {
      setLoadingContact(false);
    }
  }, [clientId, clientName, apiUrl]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = clientId 
        ? `${apiUrl}/api/invoices?clientId=${clientId}`
        : `${apiUrl}/api/invoices`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        // If no clientId, filter by client name
        let filteredInvoices = data.data || [];
        if (!clientId && clientName) {
          filteredInvoices = filteredInvoices.filter((inv: Invoice) => {
            const invClient = typeof inv === 'object' && 'clientId' in inv 
              ? (inv as any).clientId 
              : null;
            return invClient && typeof invClient === 'object' && invClient.name === clientName;
          });
        }
        setInvoices(filteredInvoices);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, clientName, apiUrl]);

  const fetchBills = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const url = clientId 
        ? `${apiUrl}/api/bills?supplierId=${clientId}`
        : `${apiUrl}/api/bills`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        // If no clientId, filter by supplier name
        let filteredBills = data.data || [];
        if (!clientId && clientName) {
          filteredBills = filteredBills.filter((bill: Bill) => {
            const billSupplier = typeof bill === 'object' && 'supplierId' in bill 
              ? (bill as any).supplierId 
              : null;
            return billSupplier && typeof billSupplier === 'object' && billSupplier.name === clientName;
          });
        }
        setBills(filteredBills);
      }
    } catch (error) {
      console.error('Failed to fetch bills:', error);
    }
  }, [clientId, clientName, apiUrl]);

  const handleCreateContact = useCallback(async () => {
    if (!clientName) return;

    setCreatingContact(true);
    try {
      const token = localStorage.getItem('token');
      
      // Try to extract email/phone from bills (for suppliers) or invoices (for clients)
      let email: string | undefined;
      let phone: string | undefined;
      
      if (isSupplier && bills.length > 0) {
        const firstBill = bills[0] as any;
        if (firstBill.supplierId && typeof firstBill.supplierId === 'object') {
          email = firstBill.supplierId.email;
          phone = firstBill.supplierId.phone;
        }
      } else if (!isSupplier && invoices.length > 0) {
        const firstInvoice = invoices[0] as any;
        if (firstInvoice.clientId && typeof firstInvoice.clientId === 'object') {
          email = firstInvoice.clientId.email;
          phone = firstInvoice.clientId.phone;
        }
      }

      const payload: any = {
        name: clientName,
        isSupplier: isSupplier,
        isClient: !isSupplier,
      };

      if (email) payload.email = email;
      if (phone) payload.phone = phone;

      const response = await fetch(`${apiUrl}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        // Refresh contact data
        await fetchContact();
      } else {
        alert(data.message || t('admin.clientDetails.createContactFailed'));
      }
    } catch (error) {
      console.error('Failed to create contact:', error);
      alert(t('admin.clientDetails.createContactFailed'));
    } finally {
      setCreatingContact(false);
    }
  }, [clientName, isSupplier, bills, invoices, apiUrl, fetchContact, t]);

  useEffect(() => {
    if (isOpen) {
      fetchContact();
      fetchInvoices();
      if (isSupplier) {
        fetchBills();
      }
    } else {
      // Reset state when modal closes
      setContact(null);
      setInvoices([]);
      setBills([]);
    }
  }, [isOpen, clientId, clientName, isSupplier, fetchContact, fetchInvoices, fetchBills]);

  const formatCurrency = (amount: number, currencyCode?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD'
    }).format(amount);
  };

  const formatDateLocal = (date?: Date | string) => {
    if (!date) return '—';
    return formatDateUtil(date);
  };

  const getStatusConfig = (status: string, amountDue?: number) => {
    if (status === 'PAID' || (amountDue !== undefined && amountDue === 0)) {
      return {
        icon: CheckCircle,
        color: 'text-success-600 dark:text-success-400',
        bg: 'bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50',
        border: 'border-green-200 dark:border-green-800',
        label: 'Paid'
      };
    }
    if (status === 'APPROVED' || status === 'SUBMITTED') {
      return {
        icon: Clock,
        color: 'text-primary-600 dark:text-primary-400',
        bg: 'bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50',
        border: 'border-primary-200 dark:border-primary-800',
        label: 'Unpaid'
      };
    }
    if (status === 'VOIDED' || status === 'DELETED') {
      return {
        icon: X,
        color: 'text-error-600 dark:text-error-400',
        bg: 'bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50',
        border: 'border-red-200 dark:border-red-800',
        label: status
      };
    }
    return {
      icon: AlertCircle,
      color: 'text-gray-600 dark:text-gray-400',
      bg: 'bg-gray-50 dark:bg-gray-800/60',
      border: 'border-gray-200 dark:border-gray-700',
      label: status
    };
  };

  if (!isOpen) return null;

  const paidInvoices = invoices.filter(inv => inv.status === 'PAID' || (inv.amountDue !== undefined && inv.amountDue === 0));
  const unpaidInvoices = invoices.filter(inv => inv.status !== 'PAID' && inv.status !== 'VOIDED' && inv.status !== 'DELETED' && (inv.amountDue === undefined || inv.amountDue > 0));
  
  const paidBills = bills.filter(bill => bill.status === 'PAID' || (bill.amountDue !== undefined && bill.amountDue === 0));
  const unpaidBills = bills.filter(bill => bill.status !== 'PAID' && bill.status !== 'VOIDED' && bill.status !== 'DELETED' && (bill.amountDue === undefined || bill.amountDue > 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${isSupplier ? 'bg-purple-100 dark:bg-gray-700 dark:border dark:border-purple-800/50' : 'bg-accent-100 dark:bg-gray-700 dark:border dark:border-accent-800/50'} rounded-lg flex items-center justify-center`}>
              {isSupplier ? (
                <Receipt className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              ) : (
                <FileText className="w-5 h-5 text-accent-600 dark:text-accent-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {clientName}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isSupplier ? t('admin.bills.supplier') : t('admin.invoices.client')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Contact Information */}
          {loadingContact ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-accent-600"></div>
            </div>
          ) : contact ? (
            <div className="mb-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-2">
                {t('contact.basicInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.type')}</div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {contact.isClient && contact.isSupplier ? t('contact.both') :
                       contact.isClient ? t('contact.client') :
                       contact.isSupplier ? t('contact.supplier') :
                       t('contact.contact')}
                    </div>
                  </div>
                </div>

                {contact.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.email')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{contact.email}</div>
                    </div>
                  </div>
                )}

                {contact.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.phone')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{contact.phone}</div>
                    </div>
                  </div>
                )}

                {contact.address?.formatted && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('contact.address')}</div>
                      <div className="text-gray-900 dark:text-gray-100">{contact.address.formatted}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm text-warning-800 dark:text-warning-400 mb-2">
                    {t('admin.clientDetails.notConnected')}
                  </p>
                  <p className="text-xs text-warning-700 dark:text-warning-300">
                    {t('admin.clientDetails.createContactHint')}
                  </p>
                </div>
                <button
                  onClick={handleCreateContact}
                  disabled={creatingContact}
                  className="flex items-center gap-2 px-3 py-1.5 bg-warning-600 hover:bg-warning-700 disabled:bg-gray-400 dark:bg-warning-600 dark:hover:bg-warning-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                >
                  {creatingContact ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('admin.clientDetails.creating')}</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>{t('admin.clientDetails.createContact')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Invoices Section */}
          {!isSupplier && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.invoices.title')}
                </h3>
                {invoices.length > 0 && (
                  <div className="flex gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-lg font-bold text-success-600 dark:text-success-400">{paidInvoices.length}</div>
                      <div className="text-gray-600 dark:text-gray-400">{t('admin.clientDetails.paid')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-primary-600 dark:text-primary-400">{unpaidInvoices.length}</div>
                      <div className="text-gray-600 dark:text-gray-400">{t('admin.clientDetails.unpaid')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-600 dark:text-gray-400">{invoices.length}</div>
                      <div className="text-gray-600 dark:text-gray-400">{t('admin.clientDetails.total')}</div>
                    </div>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">{t('admin.invoices.loading')}</p>
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('admin.invoices.noInvoices')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => {
                    const config = getStatusConfig(invoice.status, invoice.amountDue);
                    const StatusIcon = config.icon;

                    return (
                      <div
                        key={invoice._id}
                        className={`border rounded-lg p-4 ${config.border} ${config.bg} hover:shadow-md transition-shadow`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <StatusIcon className={`w-5 h-5 ${config.color} mt-0.5 flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  {invoice.invoiceNumber}
                                </h3>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${config.color}`}>
                                  {config.label}
                                </span>
                              </div>
                              
                              {invoice.reference && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                  {t('admin.clientDetails.reference')}: {invoice.reference}
                                </p>
                              )}

                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>{t('admin.invoices.tableHeaders.date')}: {formatDateLocal(invoice.date)}</span>
                                </div>
                                <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>{t('admin.invoices.tableHeaders.dueDate')}: {formatDateLocal(invoice.dueDate)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                              {formatCurrency(invoice.total, invoice.currencyCode)}
                            </div>
                            {invoice.amountDue !== undefined && invoice.amountDue > 0 && (
                              <div className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                                {t('admin.invoices.tableHeaders.amountDue')}: {formatCurrency(invoice.amountDue, invoice.currencyCode)}
                              </div>
                            )}
                            {invoice.amountPaid !== undefined && invoice.amountPaid > 0 && (
                              <div className="text-xs text-success-600 dark:text-success-400">
                                {t('admin.clientDetails.paid')}: {formatCurrency(invoice.amountPaid, invoice.currencyCode)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Bills Section */}
          {isSupplier && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.bills.title')}
                </h3>
                {bills.length > 0 && (
                  <div className="flex gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-lg font-bold text-success-600 dark:text-success-400">{paidBills.length}</div>
                      <div className="text-gray-600 dark:text-gray-400">{t('admin.clientDetails.paid')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-primary-600 dark:text-primary-400">{unpaidBills.length}</div>
                      <div className="text-gray-600 dark:text-gray-400">{t('admin.clientDetails.unpaid')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-600 dark:text-gray-400">{bills.length}</div>
                      <div className="text-gray-600 dark:text-gray-400">{t('admin.clientDetails.total')}</div>
                    </div>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">{t('admin.bills.loading')}</p>
                </div>
              ) : bills.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t('admin.bills.noBills')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bills.map((bill) => {
                    const config = getStatusConfig(bill.status, bill.amountDue);
                    const StatusIcon = config.icon;

                    return (
                      <div
                        key={bill._id}
                        className={`border rounded-lg p-4 ${config.border} ${config.bg} hover:shadow-md transition-shadow`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <StatusIcon className={`w-5 h-5 ${config.color} mt-0.5 flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  {bill.billNumber}
                                </h3>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${config.color}`}>
                                  {config.label}
                                </span>
                              </div>
                              
                              {bill.reference && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                  {t('admin.clientDetails.reference')}: {bill.reference}
                                </p>
                              )}

                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>{t('admin.bills.tableHeaders.date')}: {formatDateLocal(bill.date)}</span>
                                </div>
                                <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>{t('admin.bills.tableHeaders.dueDate')}: {formatDateLocal(bill.dueDate)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                              {formatCurrency(bill.total, bill.currencyCode)}
                            </div>
                            {bill.amountDue !== undefined && bill.amountDue > 0 && (
                              <div className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                                {t('admin.bills.tableHeaders.amountDue')}: {formatCurrency(bill.amountDue, bill.currencyCode)}
                              </div>
                            )}
                            {bill.amountPaid !== undefined && bill.amountPaid > 0 && (
                              <div className="text-xs text-success-600 dark:text-success-400">
                                {t('admin.clientDetails.paid')}: {formatCurrency(bill.amountPaid, bill.currencyCode)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

